import React, { useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RupiahInput } from "@/components/RupiahInput";
import { FileUploader } from "@/components/FileUploader";
import { SELECT_CLS } from "@/components/SalesShared";
import { useFundSources } from "@/components/FundSource";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Wallet, ExternalLink, AlertTriangle, X, Layers } from "lucide-react";
import { toast } from "sonner";

const accText = (a) => (a.account_number === "TUNAI" ? "Tunai Pusat" : [a.bank_name, a.account_number, a.account_holder ? `a.n. ${a.account_holder}` : ""].filter(Boolean).join(" "));

export function PartialPayments({ b }) {
  const parts = b.partial_payments || [];
  if (!parts.length) return null;
  return (
    <div className="mt-1 space-y-0.5" data-testid={`bill-partials-${b.id}`}>
      {parts.map((p) => (
        <p key={p.id} className="text-[11px] text-emerald-800 flex flex-wrap items-center gap-1">
          <span>{p.bulk_id ? "Bayar Massal" : "Bayar Sebagian"} <b className="font-mono">{formatRupiah(p.amount)}</b> · {formatDate(p.paid_at)}</span>
          {p.pay_account && <span className="font-mono">· {p.pay_account.company_name ? `${p.pay_account.company_name} · ` : ""}{accText(p.pay_account)}</span>}
          {p.paid_by && <span>· oleh {p.paid_by}</span>}
          {p.proof_file_id && <a href={fileUrl(p.proof_file_id)} target="_blank" rel="noreferrer" className="text-brand hover:underline inline-flex items-center gap-0.5">Bukti <ExternalLink className="h-3 w-3" /></a>}
        </p>
      ))}
    </div>
  );
}

export function PartialPayDialog({ bill, onDone, AccountSelect }) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accNo, setAccNo] = useState("");
  const [amt, setAmt] = useState("");
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rem = bill.remaining_amount ?? bill.amount;
  const n = Number(amt) || 0;
  const onOpen = async (o) => {
    setOpen(o);
    if (!o) return;
    setAmt(""); setFiles([]); setNote("");
    try {
      const res = await api.get(`/vendor-bills/${bill.id}/accounts`);
      const accs = res.data.accounts || [];
      const all = res.data.cash_balance != null ? [{ bank_name: "Tunai Pusat", account_number: "TUNAI", account_holder: "", balance: res.data.cash_balance, is_cash: true }, ...accs] : accs;
      setAccounts(all); setAccNo(all.length === 1 ? all[0].account_number : "");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const submit = async () => {
    if (n <= 0 || n > rem + 0.001) { toast.error(`Nominal harus antara Rp 1 dan sisa ${formatRupiah(rem)}`); return; }
    if (accounts.length && !accNo) { toast.error("Pilih sumber pembayaran"); return; }
    const a = accounts.find((x) => x.account_number === accNo);
    if (a?.is_cash && a.balance + 0.001 < n) { toast.error("Saldo Tunai Pusat tidak mencukupi"); return; }
    if (!files.length) { toast.error("Upload bukti pembayaran"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/vendor-bills/${bill.id}/pay-partial`, { amount: n, account_number: accNo, file_id: files[0].file_id, note });
      toast.success(r.data.status === "paid" ? "Tagihan lunas." : `Pembayaran tercatat. Sisa ${formatRupiah(r.data.remaining_amount)}.`);
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-brand/40 text-brand" data-testid={`partial-pay-btn-${bill.id}`}><Wallet className="h-4 w-4 mr-1" /> Bayar Sebagian</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`partial-pay-dialog-${bill.id}`}>
        <DialogHeader>
          <DialogTitle>Bayar Sebagian — {bill.vendor_name}</DialogTitle>
          <DialogDescription>Total {formatRupiah(bill.amount)} · Terbayar {formatRupiah(bill.paid_amount || 0)} · Sisa <b>{formatRupiah(rem)}</b></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nominal Dibayar <span className="text-red-500">*</span></Label>
            <RupiahInput value={amt} onChange={(e) => setAmt(e.target.value)} className="mt-1.5 font-mono" data-testid={`partial-amount-${bill.id}`} />
            <div className="flex gap-2 mt-2">
              <Button type="button" size="sm" variant="secondary" onClick={() => setAmt(String(Math.round(rem / 2)))} data-testid={`partial-half-${bill.id}`}>Separuh (50%)</Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => setAmt(String(rem))} data-testid={`partial-full-${bill.id}`}>Lunasi Sisa</Button>
            </div>
            {n > 0 && <p className="text-xs text-muted-foreground mt-1.5" data-testid={`partial-after-${bill.id}`}>Sisa setelah bayar: <span className="font-mono font-semibold">{formatRupiah(Math.max(rem - n, 0))}</span></p>}
          </div>
          <AccountSelect accounts={accounts} value={accNo} onChange={setAccNo} amount={n} testId={`partial-account-${bill.id}`} />
          <div><Label>Upload Bukti Pembayaran <span className="text-red-500">*</span></Label>
            <div className="mt-1.5"><FileUploader files={files} onChange={setFiles} testId={`partial-uploader-${bill.id}`} /></div></div>
          <div><Label>Catatan (opsional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid={`partial-note-${bill.id}`} /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid={`partial-submit-${bill.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konfirmasi Pembayaran"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkPayDialog({ bills, total, onDone }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ method: "transfer", company_id: "", account_number: "" });
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const sources = useFundSources(open);
  const pt = (sources || []).find((s) => s.company_id === v.company_id);
  const acc = pt?.accounts.find((a) => a.account_number === v.account_number);
  const pooled = sources?.[0]?.pooled_cash_balance ?? 0;
  const bal = v.method === "tunai" ? pooled : acc?.balance;
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value, ...(k !== "account_number" ? { account_number: "" } : {}) }));
  const submit = async () => {
    if (v.method === "transfer" && (!v.company_id || !v.account_number)) { toast.error("Pilih PT dan rekening"); return; }
    if (v.method === "tunai" && pooled + 0.001 < total) { toast.error("Saldo Kas Tunai Pusat tidak mencukupi"); return; }
    if (!files.length) { toast.error("Upload bukti pembayaran"); return; }
    setBusy(true);
    try {
      const r = await api.post("/vendor-bills/pay-bulk", { bill_ids: bills.map((b) => b.id), ...v, file_id: files[0].file_id, note });
      toast.success(`${r.data.paid_count} tagihan lunas (${formatRupiah(r.data.total)}).`);
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setV({ method: "transfer", company_id: "", account_number: "" }); setFiles([]); setNote(""); } }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand hover:bg-emerald-800" data-testid="bulk-pay-btn"><Layers className="h-4 w-4 mr-1.5" /> Bayar Semua</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-pay-dialog">
        <DialogHeader>
          <DialogTitle>Bayar {bills.length} Tagihan Sekaligus</DialogTitle>
          <DialogDescription>Total sisa <b data-testid="bulk-pay-total">{formatRupiah(total)}</b> dilunasi dari satu sumber dana.</DialogDescription>
        </DialogHeader>
        <div className="max-h-40 overflow-y-auto rounded-md border divide-y text-xs">
          {bills.map((b) => <div key={b.id} className="flex justify-between gap-2 px-3 py-1.5"><span className="truncate">{b.vendor_name} · {b.company_name || "Tanpa PT"}</span><span className="font-mono">{formatRupiah(b.remaining_amount)}</span></div>)}
        </div>
        {!sources ? <Loader2 className="h-5 w-5 animate-spin text-brand mx-auto" /> : (
          <div className="space-y-3">
            <div><Label className="text-xs">Metode Pembayaran</Label>
              <select value={v.method} onChange={set("method")} className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="bulk-pay-method">
                <option value="transfer">Transfer (Rekening PT)</option><option value="tunai">Kas Tunai Pusat</option>
              </select></div>
            {v.method === "transfer" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">PT</Label>
                  <select value={v.company_id} onChange={set("company_id")} className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="bulk-pay-pt">
                    <option value="">Pilih PT</option>{sources.map((s) => <option key={s.company_id} value={s.company_id}>{s.company_name}</option>)}
                  </select></div>
                <div><Label className="text-xs">Rekening</Label>
                  <select value={v.account_number} onChange={set("account_number")} disabled={!pt} className={`${SELECT_CLS} mt-1 h-9 w-full`} data-testid="bulk-pay-account">
                    <option value="">Pilih rekening</option>{(pt?.accounts || []).map((a) => <option key={a.account_number} value={a.account_number}>{accText(a)} — {formatRupiah(a.balance)}</option>)}
                  </select></div>
              </div>
            )}
            {bal != null && <p className="text-xs text-muted-foreground" data-testid="bulk-pay-balance">Saldo {v.method === "tunai" ? "Kas Tunai Pusat" : "rekening"} {formatRupiah(bal)} · setelah bayar <span className="font-mono">{formatRupiah(bal - total)}</span></p>}
            {bal != null && bal < total && (
              <div className={`flex gap-2 rounded-md border p-2 text-xs ${v.method === "tunai" ? "border-rose-300 bg-rose-50 text-rose-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="bulk-pay-short">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{v.method === "tunai" ? "Saldo Tunai Pusat tidak mencukupi. Pilih sumber lain." : "Saldo rekening tidak mencukupi; tetap bisa diproses, saldo menjadi minus."}</span>
              </div>
            )}
            <div><Label className="text-xs">Upload Bukti Pembayaran (1 bukti untuk semua) <span className="text-red-500">*</span></Label>
              <div className="mt-1"><FileUploader files={files} onChange={setFiles} testId="bulk-pay-uploader" /></div></div>
            <div><Label className="text-xs">Catatan (opsional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-9" data-testid="bulk-pay-note" /></div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !sources} className="bg-brand hover:bg-emerald-800" data-testid="bulk-pay-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konfirmasi Bayar Semua"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PayCheck({ checked, onChange, testId }) {
  return <input type="checkbox" checked={checked} onChange={onChange} title="Pilih untuk bayar massal" className="h-4 w-4 accent-emerald-700 cursor-pointer" data-testid={testId} />;
}

export function BulkPayBar({ bills, onClear, onDone }) {
  if (!bills.length) return null;
  const total = bills.reduce((s, b) => s + (b.remaining_amount || 0), 0);
  return (
    <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50/95 backdrop-blur px-4 py-2.5 shadow-sm" data-testid="bulk-pay-bar">
      <span className="text-sm"><b data-testid="bulk-pay-count">{bills.length}</b> tagihan dipilih · Total <b className="font-mono" data-testid="bulk-pay-bar-total">{formatRupiah(total)}</b></span>
      <div className="ml-auto flex gap-2">
        <Button size="sm" variant="ghost" onClick={onClear} data-testid="bulk-pay-clear"><X className="h-4 w-4 mr-1" /> Batal</Button>
        <BulkPayDialog bills={bills} total={total} onDone={() => { onClear(); onDone(); }} />
      </div>
    </div>
  );
}
